import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// const API_URL = `${process.env.REACT_APP_API_URL}/api/teacher`;
// const API_URL = "https://schoolerp-1xul.onrender.com/api/teacher";
const API_URL = `${process.env.REACT_APP_API_URL}/api/teacher`

// Returns the current academic year e.g. "2025-2026" (April start)
const currentAcademicYear = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed, March = 2, April = 3
  const start = m < 3 ? y - 1 : y;
  return `${start}-${start + 1}`;
};


// Get teacher profile
export const getTeacherProfile = createAsyncThunk(
  "teacher/getProfile",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/profile`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get assigned students
export const getAssignedStudents = createAsyncThunk(
  "teacher/getStudents",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/students`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get students by class-section
export const getStudentsByClass = createAsyncThunk(
  "teacher/getStudentsByClass",
  async ({ teacherId, classSection }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/students/${classSection}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get students for results upload (separate from attendance)
export const getStudentsForResults = createAsyncThunk(
  "teacher/getStudentsForResults",
  async ({ teacherId, classSection }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/students/${classSection}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Mark attendance
export const markAttendance = createAsyncThunk(
  "teacher/markAttendance",
  async ({ teacherId, attendanceData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${teacherId}/attendance`, attendanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Edit homework
export const editHomework = createAsyncThunk(
  "teacher/editHomework",
  async ({ teacherId, homeworkId, homeworkData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.put(
        `${API_URL}/${teacherId}/homework/${homeworkId}`,
        homeworkData,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get homework submissions
export const getHomeworkSubmissions = createAsyncThunk(
  "teacher/getHomeworkSubmissions",
  async ({ teacherId, homeworkId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/${teacherId}/homework/${homeworkId}/submissions`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get attendance
export const getAttendance = createAsyncThunk(
  "teacher/getAttendance",
  async ({ teacherId, date, classSection }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/attendance/${date}/${classSection}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Assign homework (Supports File Upload)
export const assignHomework = createAsyncThunk(
  "teacher/assignHomework",
  async ({ teacherId, homeworkData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.post(`${API_URL}/${teacherId}/homework`, homeworkData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get homework
export const getHomework = createAsyncThunk(
  "teacher/getHomework",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/homework`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Create announcement (Supports File Upload)
export const createAnnouncement = createAsyncThunk(
  "teacher/createAnnouncement",
  async ({ teacherId, announcementData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.post(`${API_URL}/${teacherId}/announcements`, announcementData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Update announcement
export const updateAnnouncement = createAsyncThunk(
  "teacher/updateAnnouncement",
  async ({ teacherId, announcementId, announcementData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.put(`${API_URL}/${teacherId}/announcements/${announcementId}`, announcementData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete announcement
export const deleteAnnouncement = createAsyncThunk(
  "teacher/deleteAnnouncement",
  async ({ teacherId, announcementId }, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${teacherId}/announcements/${announcementId}`);
      return announcementId; // Return ID to filter out from state
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Something went wrong"
      );
    }
  }
);

// Get Syllabus
export const getSyllabus = createAsyncThunk(
  "teacher/getSyllabus",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/syllabus`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Upload Syllabus
export const uploadSyllabus = createAsyncThunk(
  "teacher/uploadSyllabus",
  async ({ teacherId, syllabusData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.post(`${API_URL}/${teacherId}/syllabus`, syllabusData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Update Syllabus (PUT)
export const updateSyllabus = createAsyncThunk(
  "teacher/updateSyllabus",
  async ({ teacherId, syllabusId, syllabusData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.put(`${API_URL}/${teacherId}/syllabus/${syllabusId}`, syllabusData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete Syllabus
export const deleteSyllabus = createAsyncThunk(
  "teacher/deleteSyllabus",
  async ({ teacherId, syllabusId }, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${teacherId}/syllabus/${syllabusId}`);
      return syllabusId; // Return ID to filter out from state
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message || "Something went wrong");
    }
  }
);

// Get announcements
export const getAnnouncements = createAsyncThunk(
  "teacher/getAnnouncements",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/announcements`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get leave requests
export const getLeaveRequests = createAsyncThunk(
  "teacher/getLeaveRequests",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/leave-requests`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all leave requests (History)
export const getAllLeaveRequests = createAsyncThunk(
  "teacher/getAllLeaveRequests",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/leave-history`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Approve leave
export const approveLeave = createAsyncThunk(
  "teacher/approveLeave",
  async ({ teacherId, leaveData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${teacherId}/leave-approval`, leaveData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get dashboard stats
export const getDashboardStats = createAsyncThunk(
  "teacher/getDashboardStats",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/dashboard-stats`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ========== NEW RESULTS MANAGEMENT ACTIONS ==========

// Get marking scheme
export const getMarkingScheme = createAsyncThunk(
  "teacher/getMarkingScheme",
  async ({ teacherId, classNum, section = "All", year }, { rejectWithValue }) => {
    const academicYear = year || currentAcademicYear();
    try {
      const response = await axios.get(
        `${API_URL}/${teacherId}/marking-scheme/${classNum}?year=${academicYear}&section=${section}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Upload results
export const uploadResults = createAsyncThunk(
  "teacher/uploadResults",
  async ({ teacherId, resultsData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/${teacherId}/results/upload`,
        resultsData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get class results
export const getClassResults = createAsyncThunk(
  "teacher/getClassResults",
  async ({ teacherId, classNum, section, params = {} }, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await axios.get(
        `${API_URL}/${teacherId}/results/${classNum}/${section}?${query}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update result
export const updateResult = createAsyncThunk(
  "teacher/updateResult",
  async ({ teacherId, resultId, updateData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_URL}/${teacherId}/results/${resultId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Publish results
export const publishResults = createAsyncThunk(
  "teacher/publishResults",
  async ({ teacherId, publishData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/${teacherId}/results/publish`,
        publishData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete result
export const deleteResult = createAsyncThunk(
  "teacher/deleteResult",
  async ({ teacherId, resultId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${API_URL}/${teacherId}/results/${resultId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get results dashboard stats
export const getResultsStats = createAsyncThunk(
  "teacher/getResultsStats",
  async ({ teacherId, year }, { rejectWithValue }) => {
    const academicYear = year || currentAcademicYear();
    try {
      const response = await axios.get(
        `${API_URL}/${teacherId}/results/stats/dashboard?year=${academicYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get results history
export const getResultsHistory = createAsyncThunk(
  "teacher/getResultsHistory",
  async ({ teacherId, year }, { rejectWithValue }) => {
    const academicYear = year || currentAcademicYear();
    try {
      const response = await axios.get(
        `${API_URL}/${teacherId}/results/history?year=${academicYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete entire results session
export const deleteResultSession = createAsyncThunk(
  "teacher/deleteResultSession",
  async ({ teacherId, params }, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await axios.delete(
        `${API_URL}/${teacherId}/results/session?${query}`
      );
      return { ...response.data, params }; // Return params to help identify which one was deleted
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get class performance comparison
export const getClassComparison = createAsyncThunk(
  "teacher/getClassComparison",
  async ({ teacherId, params = {} }, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await axios.get(
        `${API_URL}/${teacherId}/results/stats/class-comparison?${query}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get Attendance Summary (Weekly/Monthly/Custom)
export const getAttendanceSummary = createAsyncThunk(
  "teacher/getAttendanceSummary",
  async ({ teacherId, classSection, ...params }, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams({
        classSection,
        range: params.range || "overall",
        ...params
      }).toString();
      const response = await axios.get(`${API_URL}/${teacherId}/attendance-summary?${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Approved Leaves
export const getApprovedLeaves = createAsyncThunk(
  "teacher/getApprovedLeaves",
  async ({ teacherId, date, classSection }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/${teacherId}/approved-leaves?date=${date}&classSection=${classSection}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


// Helper for teacher
const getInitialProfile = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return (user && user.role === "teacher") ? user : null;
  } catch (error) {
    return null;
  }
};

// ==========================================
// NEW: TEACHER SELF-ATTENDANCE THUNKS
// ==========================================

// Mark Self Attendance
export const markSelfAttendance = createAsyncThunk(
  "teacher/markSelfAttendance",
  async ({ teacherId, attendanceData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${teacherId}/self-attendance`, attendanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Self Attendance History
export const getSelfAttendance = createAsyncThunk(
  "teacher/getSelfAttendance",
  async ({ teacherId, startDate, endDate }, { rejectWithValue }) => {
    try {
      let query = "";
      if (startDate && endDate) {
        query = `?startDate=${startDate}&endDate=${endDate}`;
      }
      const response = await axios.get(`${API_URL}/${teacherId}/self-attendance${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Check Self Backlog Status
export const checkSelfBacklogStatus = createAsyncThunk(
  "teacher/checkSelfBacklogStatus",
  async ({ teacherId, date }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/self-backlog-status/${date}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Request Self Attendance Backlog
export const requestSelfAttendanceBacklog = createAsyncThunk(
  "teacher/requestSelfAttendanceBacklog",
  async ({ teacherId, requestData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${teacherId}/self-attendance-backlog`, requestData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Self Attendance Backlogs History
export const getSelfAttendanceBacklogs = createAsyncThunk(
  "teacher/getSelfAttendanceBacklogs",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/self-attendance-backlog`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ==========================================
// TEACHER LEAVES TO ADMIN
// ==========================================

export const applyForSelfLeave = createAsyncThunk(
  "teacher/applyForSelfLeave",
  async ({ teacherId, leaveData }, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = await axios.post(`${API_URL}/${teacherId}/leave`, leaveData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getSelfLeaves = createAsyncThunk(
  "teacher/getSelfLeaves",
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${teacherId}/leaves`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ==========================================

const initialState = {
  profile: getInitialProfile(),
  students: [],
  selectedClassStudents: [],
  attendance: [],
  attendanceSummary: [], // Added for overview
  homework: [],
  submissions: [], // For viewing homework submissions
  syllabus: [], // For assigned syllabus
  lessonPlans: { data: [], totalPages: 1, limit: 10, total: 0 },
  announcements: [],
  leaveRequests: [], // Pending requests
  allLeaveRequests: [], // Approved, Rejected, Pending
  approvedLeaves: [], // New state for auto-marking
  dashboardStats: null,
  // results state
  markingScheme: null,
  resultsStudents: [], // Separate state for results upload
  classResults: [],
  resultsStats: null,
  classComparison: [],
  resultsHistory: [],
  timetable: null,

  // my leaves state
  myLeavesHistory: [],

  // self attendance state
  selfAttendanceHistory: [],
  selfAttendanceBacklogs: [],
  selfBacklogStatus: null,

  loadings: {
    profile: false,
    students: false,
    stats: false,
    markingScheme: false,
    results: false,
    leaves: false,
    attendance: false,
    syllabus: false,
    lessonPlans: false,
    announcements: false,
    homework: false
  },
  loading: false, // Global/Form loading
  error: null,
  success: false,
};


// Save/Update Timetable
export const saveTimetable = createAsyncThunk(
  "teacher/saveTimetable",
  async ({ teacherId, classSection, timetable }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/${teacherId}/timetable`,
        { classSection, timetable }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

// Get Timetable
export const getTimetable = createAsyncThunk(
  "teacher/getTimetable",
  async ({ teacherId, classSection }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/${teacherId}/timetable/${classSection}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

// ==========================================
// ENTERPRISE LESSON PLAN ACTIONS
// ==========================================
// Since it's a unified endpoint, we use the base API_URL without trailing /teacher
const LESSON_PLAN_API = `${process.env.REACT_APP_API_URL}/api/lesson-plans`;

export const createLessonPlan = createAsyncThunk("teacher/createLessonPlan", async (formData, { rejectWithValue }) => {
  try {
    const config = { headers: { "Content-Type": "multipart/form-data" } };
    const response = await axios.post(LESSON_PLAN_API, formData, config);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

export const bulkCreateLessonPlans = createAsyncThunk("teacher/bulkCreateLessonPlans", async (payload, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${LESSON_PLAN_API}/bulk`, payload);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

export const getLessonPlans = createAsyncThunk("teacher/getLessonPlans", async (params = {}, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params).toString();
    const response = await axios.get(`${LESSON_PLAN_API}?${query}`);
    return response.data; // Will contain { data, total, page, limit, totalPages }
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

export const updateLessonPlan = createAsyncThunk("teacher/updateLessonPlan", async ({ id, formData }, { rejectWithValue }) => {
  try {
    const config = { headers: { "Content-Type": "multipart/form-data" } };
    const response = await axios.put(`${LESSON_PLAN_API}/${id}`, formData, config);
    return response.data;
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

export const deleteLessonPlan = createAsyncThunk("teacher/deleteLessonPlan", async (id, { rejectWithValue }) => {
  try {
    await axios.delete(`${LESSON_PLAN_API}/${id}`);
    return id;
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

export const submitLessonPlan = createAsyncThunk("teacher/submitLessonPlan", async ({ id, teacherId }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${LESSON_PLAN_API}/${id}/submit`, { teacher_id: teacherId });
    return { ...response.data, id };
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

export const updateLessonExecution = createAsyncThunk("teacher/updateLessonExecution", async ({ id, executionData }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${LESSON_PLAN_API}/${id}/execution`, executionData);
    return { ...response.data, id, status: executionData.status };
  } catch (error) { return rejectWithValue(error.response?.data || error.message); }
});

const teacherSlice = createSlice({
  name: "teacher",
  initialState,
  reducers: {
    resetTeacher: (state) => {
      // ... existing reset logic
      state.profile = null;
      state.students = [];
      state.selectedClassStudents = [];
      state.attendance = [];
      state.attendanceSummary = [];
      state.homework = [];
      state.syllabus = [];
      state.lessonPlans = { data: [], totalPages: 1, limit: 10, total: 0 };
      state.announcements = [];
      state.leaveRequests = [];
      state.allLeaveRequests = [];
      state.dashboardStats = null;
      //result
      state.markingScheme = null;
      state.classResults = [];
      state.resultsStats = null;
      state.classComparison = [];
      state.resultsHistory = [];

      state.selfAttendanceHistory = [];
      state.selfAttendanceBacklogs = [];
      state.selfBacklogStatus = null;

      state.myLeavesHistory = [];

      state.error = null;
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ... existing reducers ...
      // Get Profile
      .addCase(getTeacherProfile.pending, (state) => {
        state.loadings.profile = true;
      })
      .addCase(getTeacherProfile.fulfilled, (state, action) => {
        state.loadings.profile = false;
        state.profile = action.payload;
      })
      .addCase(getTeacherProfile.rejected, (state, action) => {
        state.loadings.profile = false;
        state.error = action.payload;
      })

      // Get Students
      .addCase(getAssignedStudents.pending, (state) => {
        state.loadings.students = true;
      })
      .addCase(getAssignedStudents.fulfilled, (state, action) => {
        state.loadings.students = false;
        state.students = action.payload;
      })
      .addCase(getAssignedStudents.rejected, (state, action) => {
        state.loadings.students = false;
        state.error = action.payload;
      })

      // Get Students by Class
      .addCase(getStudentsByClass.pending, (state) => {
        state.loadings.students = true;
      })
      .addCase(getStudentsByClass.fulfilled, (state, action) => {
        state.loadings.students = false;
        state.selectedClassStudents = action.payload;
      })
      .addCase(getStudentsByClass.rejected, (state, action) => {
        state.loadings.students = false;
        state.error = action.payload;
      })

      // Get Students for Results Upload (separate state)
      .addCase(getStudentsForResults.pending, (state) => {
        state.loadings.students = true;
      })
      .addCase(getStudentsForResults.fulfilled, (state, action) => {
        state.loadings.students = false;
        state.resultsStudents = action.payload;
      })
      .addCase(getStudentsForResults.rejected, (state, action) => {
        state.loadings.students = false;
        state.error = action.payload;
      })

      // Mark Attendance
      .addCase(markAttendance.pending, (state) => {
        state.loading = true;
      })
      .addCase(markAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(markAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Attendance
      .addCase(getAttendance.fulfilled, (state, action) => {
        state.attendance = action.payload;
      })

      // Get Attendance Summary (NEW)
      .addCase(getAttendanceSummary.pending, (state) => {
        state.loadings.attendance = true;
      })
      .addCase(getAttendanceSummary.fulfilled, (state, action) => {
        state.loadings.attendance = false;
        state.attendanceSummary = action.payload;
      })
      .addCase(getAttendanceSummary.rejected, (state, action) => {
        state.loadings.attendance = false;
        state.error = action.payload;
      })

      // Get Approved Leaves
      .addCase(getApprovedLeaves.fulfilled, (state, action) => {
        state.approvedLeaves = action.payload;
      })

      // Assign Homework
      .addCase(assignHomework.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignHomework.fulfilled, (state, action) => {
        console.log("HOMEWORK PAYLOAD 👉", action.payload);
        state.loading = false;
        state.success = true;
      })
      .addCase(assignHomework.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit Homework
      .addCase(editHomework.pending, (state) => {
        state.loading = true;
      })
      .addCase(editHomework.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(editHomework.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Homework Submissions
      .addCase(getHomeworkSubmissions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHomeworkSubmissions.fulfilled, (state, action) => {
        state.loading = false;
        state.submissions = action.payload;
      })
      .addCase(getHomeworkSubmissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Homework
      .addCase(getHomework.fulfilled, (state, action) => {
        state.homework = action.payload;
      })

      // Create Announcement
      .addCase(createAnnouncement.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Announcement
      .addCase(updateAnnouncement.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Announcement
      .addCase(deleteAnnouncement.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.announcements = state.announcements.filter(a => a._id !== action.payload);
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Syllabus
      .addCase(getSyllabus.pending, (state) => {
        state.loadings.syllabus = true;
      })
      .addCase(getSyllabus.fulfilled, (state, action) => {
        state.loadings.syllabus = false;
        state.syllabus = action.payload;
      })
      .addCase(getSyllabus.rejected, (state, action) => {
        state.loadings.syllabus = false;
        state.error = action.payload;
      })

      // Upload Syllabus
      .addCase(uploadSyllabus.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadSyllabus.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(uploadSyllabus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Syllabus
      .addCase(updateSyllabus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSyllabus.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateSyllabus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Syllabus
      .addCase(deleteSyllabus.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSyllabus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.syllabus = state.syllabus.filter(s => s._id !== action.payload);
      })
      .addCase(deleteSyllabus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Announcements
      .addCase(getAnnouncements.fulfilled, (state, action) => {
        state.announcements = action.payload;
      })

      .addCase(getLeaveRequests.fulfilled, (state, action) => {
        state.leaveRequests = action.payload;
      })

      // Get All Leave Requests (History)
      .addCase(getAllLeaveRequests.pending, (state) => {
        state.loadings.leaves = true;
      })
      .addCase(getAllLeaveRequests.fulfilled, (state, action) => {
        state.loadings.leaves = false;
        state.allLeaveRequests = action.payload;
      })
      .addCase(getAllLeaveRequests.rejected, (state, action) => {
        state.loadings.leaves = false;
        state.error = action.payload;
      })

      // Approve Leave
      .addCase(approveLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveLeave.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(approveLeave.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Dashboard Stats
      .addCase(getDashboardStats.fulfilled, (state, action) => {
        state.dashboardStats = action.payload;
      })
      // ========== NEW RESULTS REDUCERS ==========

      // Get Marking Scheme
      .addCase(getMarkingScheme.pending, (state) => {
        state.loadings.markingScheme = true;
      })
      .addCase(getMarkingScheme.fulfilled, (state, action) => {
        state.loadings.markingScheme = false;
        state.markingScheme = action.payload;
      })
      .addCase(getMarkingScheme.rejected, (state, action) => {
        state.loadings.markingScheme = false;
        state.error = action.payload;
      })

      // Upload Results
      .addCase(uploadResults.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadResults.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(uploadResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Class Results
      .addCase(getClassResults.pending, (state) => {
        state.loadings.results = true;
      })
      .addCase(getClassResults.fulfilled, (state, action) => {
        state.loadings.results = false;
        state.classResults = action.payload;
      })
      .addCase(getClassResults.rejected, (state, action) => {
        state.loadings.results = false;
        state.error = action.payload;
      })

      // ==========================================
      // NEW: TEACHER SELF-ATTENDANCE REDUCERS
      // ==========================================

      // Mark Self Attendance
      .addCase(markSelfAttendance.pending, (state) => {
        state.loading = true;
      })
      .addCase(markSelfAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(markSelfAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Self Attendance History
      .addCase(getSelfAttendance.pending, (state) => {
        state.loadings.attendance = true;
      })
      .addCase(getSelfAttendance.fulfilled, (state, action) => {
        state.loadings.attendance = false;
        state.selfAttendanceHistory = action.payload;
      })
      .addCase(getSelfAttendance.rejected, (state, action) => {
        state.loadings.attendance = false;
        state.error = action.payload;
      })

      // Check Self Backlog Status
      .addCase(checkSelfBacklogStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkSelfBacklogStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.selfBacklogStatus = action.payload;
      })
      .addCase(checkSelfBacklogStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Request Self Attendance Backlog
      .addCase(requestSelfAttendanceBacklog.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestSelfAttendanceBacklog.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(requestSelfAttendanceBacklog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Result
      .addCase(updateResult.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateResult.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateResult.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Publish Results
      .addCase(publishResults.pending, (state) => {
        state.loading = true;
      })
      .addCase(publishResults.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(publishResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Result
      .addCase(deleteResult.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteResult.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteResult.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Results Stats
      .addCase(getResultsStats.fulfilled, (state, action) => {
        state.resultsStats = action.payload;
      })

      // Get Class Comparison
      .addCase(getClassComparison.fulfilled, (state, action) => {
        state.classComparison = action.payload;
      })

      // Results History
      .addCase(getResultsHistory.pending, (state) => {
        state.loadings.results = true;
      })
      .addCase(getResultsHistory.fulfilled, (state, action) => {
        state.loadings.results = false;
        state.resultsHistory = action.payload;
      })
      .addCase(getResultsHistory.rejected, (state, action) => {
        state.loadings.results = false;
        state.error = action.payload;
      })

      // Delete Result Session
      .addCase(deleteResultSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteResultSession.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Optimization: locally filter history
        const { params } = action.payload;
        state.resultsHistory = state.resultsHistory.filter(h =>
          !(h.exam_id === params.exam_id &&
            h.subject === params.subject &&
            h.class === params.class &&
            h.section === params.section)
        );
      })
      .addCase(deleteResultSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Save Timetable
      .addCase(saveTimetable.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveTimetable.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Optionally update local state if needed
      })
      .addCase(saveTimetable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Timetable
      .addCase(getTimetable.pending, (state) => {
        state.loadings.timetable = true;
      })
      .addCase(getTimetable.fulfilled, (state, action) => {
        state.loadings.timetable = false;
        state.timetable = action.payload;
      })
      .addCase(getTimetable.rejected, (state, action) => {
        state.loadings.timetable = false;
        state.error = action.payload;
      })
      // Get Self Attendance Backlogs
      .addCase(getSelfAttendanceBacklogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSelfAttendanceBacklogs.fulfilled, (state, action) => {
        state.loading = false;
        state.selfAttendanceBacklogs = action.payload;
      })
      .addCase(getSelfAttendanceBacklogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Teacher Leaves
      .addCase(applyForSelfLeave.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyForSelfLeave.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = action.payload.message;
      })
      .addCase(applyForSelfLeave.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to apply for leave";
      })
      .addCase(getSelfLeaves.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSelfLeaves.fulfilled, (state, action) => {
        state.loading = false;
        state.myLeavesHistory = action.payload;
      })
      .addCase(getSelfLeaves.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch leave history";
      })
      
      // ==========================================
      // ENTERPRISE LESSON PLAN REDUCERS
      // ==========================================
      .addCase(getLessonPlans.pending, (state) => {
        state.loadings.lessonPlans = true;
      })
      .addCase(getLessonPlans.fulfilled, (state, action) => {
        state.loadings.lessonPlans = false;
        state.lessonPlans = action.payload; // expects { data, total, page, limit, totalPages }
      })
      .addCase(getLessonPlans.rejected, (state, action) => {
        state.loadings.lessonPlans = false;
        state.error = action.payload;
      })
      // creation
      .addCase(createLessonPlan.pending, (state) => { state.loading = true; })
      .addCase(createLessonPlan.fulfilled, (state) => { state.loading = false; state.success = true; })
      .addCase(createLessonPlan.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // bulk
      .addCase(bulkCreateLessonPlans.pending, (state) => { state.loading = true; })
      .addCase(bulkCreateLessonPlans.fulfilled, (state) => { state.loading = false; state.success = true; })
      .addCase(bulkCreateLessonPlans.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // edit
      .addCase(updateLessonPlan.pending, (state) => { state.loading = true; })
      .addCase(updateLessonPlan.fulfilled, (state) => { state.loading = false; state.success = true; })
      .addCase(updateLessonPlan.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // submit
      .addCase(submitLessonPlan.pending, (state) => { state.loading = true; })
      .addCase(submitLessonPlan.fulfilled, (state, action) => { 
        state.loading = false; state.success = true; 
        const plan = state.lessonPlans.data.find(p => p._id === action.payload.id);
        if (plan) plan.approval_status = "Submitted";
      })
      .addCase(submitLessonPlan.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // execution
      .addCase(updateLessonExecution.pending, (state) => { state.loading = true; })
      .addCase(updateLessonExecution.fulfilled, (state, action) => { 
        state.loading = false; state.success = true; 
        const plan = state.lessonPlans.data.find(p => p._id === action.payload.id);
        if (plan) plan.status = action.payload.status;
      })
      .addCase(updateLessonExecution.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // delete
      .addCase(deleteLessonPlan.pending, (state) => { state.loading = true; })
      .addCase(deleteLessonPlan.fulfilled, (state, action) => { 
        state.loading = false; state.success = true; 
        if (state.lessonPlans && state.lessonPlans.data) {
           state.lessonPlans.data = state.lessonPlans.data.filter(p => p._id !== action.payload);
        }
      })
      .addCase(deleteLessonPlan.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { resetTeacher, clearError, clearSuccess } = teacherSlice.actions;
export default teacherSlice.reducer;